import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { ChevronDown } from 'lucide-react'

import NormalTopBar from '@/components/common/bar/NormalTopBar'
import OverlayBackground from '@/components/common/overlay/OverlayBackground'
import WideButton from '@/components/common/button/WideButton'
import { useWithdrawalMutation } from '@/hooks/queries/user/mutation'
import { useUserStore } from '@/store/userStore'
import { useErrorHandler } from '@/contexts/ErrorContext'
import { handleError } from '@/utils/axiosConfig'
import { getUserId, logout } from '@/utils/auth'
import { useAuthStore } from '@/store/authStore'
import { logger } from '@/utils/logger'

const REASON_OPTIONS = [
  { value: 'not-using', label: '자주 사용하지 않아요' },
  { value: 'using-difficultly', label: '사용 방법이 어려워요' },
  { value: 'dissatisfied', label: '앱에 오류가 많아요' },
  { value: 'privacy', label: '광고나 알람이 많이 와요' },
  { value: 'new-account', label: '새 계정을 만들고 싶어요' },
  { value: 'direct-input', label: '직접입력' }
]

const SecessionPage = () => {
  const navigate = useNavigate()
  const [secessionReason, setSecessionReason] = useState('')
  const [secessionReasonInput, setSecessionReasonInput] = useState('')
  const [showOverlay, setShowOverlay] = useState(false)
  const userStore = useUserStore()
  const errorHandler = useErrorHandler()

  const { mutate: withdrawal } = useWithdrawalMutation({
    onSuccess: async () => {
      logger.success('회원 탈퇴 성공')
      // 탈퇴 성공 시 로그아웃 처리 (패스코드도 함께 초기화됨)
      await logout()
    },
    onError: async error => {
      logger.error('회원 탈퇴 실패:', error)
      await handleError(error, errorHandler)
    }
  })

  const handleWithdrawal = () => {
    // 현재 액세스 토큰에서 user_id 가져오기
    const accessToken = useAuthStore.getState().getAccessToken()
    logger.debug('[회원탈퇴] 액세스 토큰 존재:', !!accessToken)

    const currentUserId = getUserId(accessToken || '')
    logger.debug('[회원탈퇴] 토큰에서 추출한 user_id:', currentUserId)
    logger.debug('[회원탈퇴] userStore의 user_id:', userStore.userInfo?.user_id)

    // userStore가 비어있거나 0인 경우 토큰에서 가져온 ID 사용
    const userId =
      userStore.userInfo?.user_id && userStore.userInfo.user_id !== 0 ? userStore.userInfo.user_id : currentUserId

    logger.info('[회원탈퇴] 최종 사용할 user_id:', userId)

    if (!userId || userId === 0) {
      logger.error('[회원탈퇴] 사용자 ID를 찾을 수 없습니다.')
      errorHandler.showErrorToast('사용자 정보를 찾을 수 없습니다. 다시 로그인해주세요.')
      return
    }

    logger.auth(`회원 탈퇴 요청 시작 - userId: ${userId}, reason: ${secessionReason}`)

    withdrawal({
      user_id: userId,
      body: { reason: secessionReason === 'direct-input' ? secessionReasonInput : secessionReason }
    })
  }

  return (
    <div className='flex flex-col h-screen'>
      <NormalTopBar
        title='회원탈퇴'
        prevButton
        onPrevButtonClick={() => navigate('/my')}
        textClassName='text-xl text-text-header font-semibold'
        boxClassName='bg-gradient-to-r from-amber-400 to-yellow-300'
      />
      <div className='flex flex-col flex-grow text-center'>
        <div className='flex-grow px-6 py-8'>
          <div className='bg-white rounded-2xl p-6 mb-6'>
            <p className='text-base text-amber-600 mb-2'>qBee</p>
            <h1 className='text-3xl font-bold mb-6 text-gray-900'>탈퇴하기</h1>

            {/* Label for accessibility, visually hidden */}
            <label htmlFor='secession-reason-select' className='sr-only'>
              회원 탈퇴 사유 선택
            </label>
            <div className='flex flex-col gap-2'>
              <div className='relative w-full'>
                <select
                  id='secession-reason-select'
                  name='secession-reason'
                  value={secessionReason}
                  onChange={e => {
                    setSecessionReasonInput('')
                    setSecessionReason(e.target.value)
                  }}
                  required
                  className='w-full p-4 pr-10 text-base border-0 border-b-2 border-amber-300 rounded-none focus:ring-0 focus:border-amber-500 appearance-none cursor-pointer bg-transparent'
                >
                  <option value='' disabled>
                    회원 탈퇴 사유를 선택해주세요
                  </option>
                  {REASON_OPTIONS.map(reason => (
                    <option key={reason.value} value={reason.value} className='text-base'>
                      {reason.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className='w-5 h-5 absolute top-1/2 right-3 -translate-y-1/2 text-amber-600' />
              </div>
              {secessionReason === 'direct-input' && (
                <textarea
                  placeholder='탈퇴 사유를 입력해주세요.'
                  className='w-full min-h-25 p-4 text-base border-2 border-amber-300 rounded-lg focus:ring-0 focus:border-amber-500 appearance-none cursor-pointer bg-white/50 placeholder-gray-500'
                  value={secessionReasonInput}
                  onChange={e => {
                    setSecessionReasonInput(e.target.value)
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {((secessionReason === 'direct-input' && secessionReasonInput.length > 0) ||
          (secessionReason !== 'direct-input' && secessionReason)) && (
          <div className='px-4 pb-6'>
            <WideButton
              handleClick={() => {
                setShowOverlay(true)
              }}
              buttonText='탈퇴하기'
            />
          </div>
        )}
      </div>
      {showOverlay && (
        <OverlayBackground>
          <div className='bg-white/95 backdrop-blur rounded-2xl p-6 flex flex-col gap-10 max-w-[300px]'>
            <div className='flex flex-col gap-5'>
              <p className='text-gray-600 text-center'>
                앞으로 더 좋은 기능들이 <br /> 업데이트 예정이에요!
              </p>
              <p className='text-gray-600 text-center'>
                그동안의 상담 기록이 <br /> 삭제 돼요😭
              </p>
              <p className='text-gray-700 text-center font-medium'>그래도 탈퇴하시나요?</p>
            </div>
            <div className='flex gap-3'>
              <button
                className='flex-1 p-3 rounded-xl bg-amber-100 text-amber-700 font-medium hover:bg-amber-200 transition-colors'
                onClick={() => {
                  setShowOverlay(false)
                }}
              >
                남을래요!
              </button>
              <button
                className='flex-1 p-3 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 transition-colors'
                onClick={() => {
                  setShowOverlay(false)
                  // handleWithdrawal 성공 시 onSuccess에서 logout()이 호출되어 모든 정리 작업 수행
                  handleWithdrawal()
                }}
              >
                네
              </button>
            </div>
          </div>
        </OverlayBackground>
      )}
    </div>
  )
}

export default SecessionPage
